import MuiMenuItem from '@material-ui/core/MenuItem';
import { AnyFieldValues } from '@monorepo/shared/componentsV2/aggregation/types';
import { SelectQueryColumnField } from '@monorepo/shared/componentsV2/fields/SelectQueryColumnField';
import TrashIcon from '@svg/trash.svg';
import { AggregationMethod, FieldIconType } from 'mapistry-shared';
import React, { useEffect, useMemo } from 'react';
import { useForm, useFormState } from 'react-final-form';
import styled from 'styled-components';
import { DeepPartial } from 'utility-types';
import { isRequired } from '../../../utils/validators';
import { IconButton } from '../../Button/IconButton';
import { SelectField } from '../../fields/SelectField';
import { TextField } from '../../fields/TextField';
import { VisuallyHidden } from '../../VisuallyHidden';
import { aggregationMethodLabels } from './consts';

const DeleteButton = styled(IconButton)<{ $labelIsShowing: boolean }>`
  min-width: 2rem;
  margin-bottom: ${({ $labelIsShowing }) =>
    $labelIsShowing
      ? '0.2rem'
      : '1.3rem'}; /* lines this up vertically with the input elements to its left */

  font-size: 0.75rem;
`;

type SelectValue = React.ComponentProps<typeof MuiMenuItem>['value'];

export type AggregationColumnOption = {
  methodsAllowedForColumn: AggregationMethod[];
  fieldIconType: FieldIconType;
  label: string;
  value: Exclude<SelectValue, undefined>;
};

interface AggregationDefinitionProps<FormValues extends AnyFieldValues> {
  allowedAggregationMethods: AggregationMethod[];
  canEdit: boolean;
  formFieldName: string;
  childFieldNameForAlias: string;
  childFieldNameForMethod: string;
  childFieldNameForColumn: string;
  getAllowedColumnOptions: (
    formValues: DeepPartial<FormValues>,
  ) => AggregationColumnOption[];
  onDelete?: () => void;
  showLabel: boolean;
}

export function AggregationDefinition<FormValues extends AnyFieldValues>({
  allowedAggregationMethods,
  canEdit,
  childFieldNameForAlias,
  childFieldNameForMethod,
  childFieldNameForColumn,
  formFieldName,
  getAllowedColumnOptions,
  onDelete,
  showLabel,
}: AggregationDefinitionProps<FormValues>) {
  const { change, getFieldState } = useForm();
  const { values: currentFormValues } = useFormState<DeepPartial<FormValues>>();
  const methodFieldState = getFieldState(
    `${formFieldName}.${childFieldNameForMethod}`,
  );
  const columnNameFieldState = getFieldState(
    `${formFieldName}.${childFieldNameForColumn}`,
  );
  const availableColumnOptions = useMemo(
    () => getAllowedColumnOptions(currentFormValues),
    [currentFormValues, getAllowedColumnOptions],
  );
  const selectedColumnOption = useMemo(
    () =>
      availableColumnOptions.find(
        (c) => c.value === columnNameFieldState?.value,
      ),
    [availableColumnOptions, columnNameFieldState?.value],
  );

  const columnOptions = useMemo(
    () =>
      availableColumnOptions
        .filter((c) => {
          if (!methodFieldState?.value) return false; // don't show any columns if no method is selected
          return c.methodsAllowedForColumn.includes(methodFieldState.value);
        })
        .sort((op1, op2) => op1.label.localeCompare(op2.label)),
    [availableColumnOptions, methodFieldState?.value],
  );

  const methodOptions = useMemo(
    () =>
      allowedAggregationMethods
        .filter((method) => {
          if (!selectedColumnOption) return true;
          return (
            selectedColumnOption.methodsAllowedForColumn.includes(method) ||
            // include count with any column so that the user can still get to it once any column has been selected
            method === AggregationMethod.COUNT
          );
        })
        .map((method) => ({
          label: aggregationMethodLabels[method],
          value: method,
        })),
    [allowedAggregationMethods, selectedColumnOption],
  );

  // Clear column input when user selects/unselects COUNT method
  const isRowCount = methodFieldState?.value === AggregationMethod.COUNT;
  useEffect(() => {
    isRowCount && change(`${formFieldName}.${childFieldNameForColumn}`, '');
  }, [change, isRowCount, formFieldName, childFieldNameForColumn]);

  return (
    <>
      <TextField
        disabled={!canEdit}
        fullWidth
        isLabelHidden={!showLabel}
        label="Aggregation Name"
        name={`${formFieldName}.${childFieldNameForAlias}`}
        placeholder="Name your new field"
        required
        validate={isRequired}
      />
      <SelectField
        disabled={!canEdit}
        fullWidth
        isLabelHidden={!showLabel}
        label="Function"
        name={`${formFieldName}.${childFieldNameForMethod}`}
        options={methodOptions}
        required
        validate={isRequired}
      />
      {!isRowCount ? (
        <SelectQueryColumnField
          disabled={!canEdit}
          fullWidth
          isLabelHidden={!showLabel}
          label="Field"
          name={`${formFieldName}.${childFieldNameForColumn}`}
          options={columnOptions}
          placeholder="Field to aggregate"
          required
          validate={isRequired}
        />
      ) : (
        <div />
      )}
      {canEdit && onDelete ? (
        <DeleteButton onClick={onDelete} $labelIsShowing={showLabel}>
          <TrashIcon />
          <VisuallyHidden>Delete aggregation</VisuallyHidden>
        </DeleteButton>
      ) : (
        <div />
      )}
    </>
  );
}
